import * as swcHelpers from '@swc/helpers';
import React__default from 'react';
import { DataLayer, CartV2Actions } from '@grandvisionhq/analytics';
import { useMutation } from '@grandvisionhq/graphql';
import { useIntl } from '@grandvisionhq/state';
import { errorsMapping } from '../mappers/error-codes.js';
import { dispatchMiniCartEvent } from '../modules/MiniCart/utils/event.js';
import { useCartStorage } from './use-cart-storage.js';
import { getValidationErrorCodes } from '../utils/get-validation-error-codes.js';
import { lineItemDataMapper } from '../utils/line-item-data-mapper.js';
import { mapLineItemToDataLayerEvent } from '../mappers/analytics/data-layer/line-item-to-data-layer-event.js';
import { createProductObject } from '../mappers/analytics/structured-data-mapper.js';
import { useCartSettings } from '../context/CartSettings/context.js';
import { REPLACE_CONTACT_LENS_BUNDLE_BENELUX_CART_LINE_ITEM } from '../operations/mutations/replace-line-item-contact-lenses-bundle-benelux.js';

const useReplaceLineItemContactLensesBundleBenelux = ()=>{
    const intl = useIntl();
    const settings = useCartSettings();
    const [cart] = useCartStorage();
    const [replaceCartLineItem, replaceCartLineItemMutation] = useMutation(REPLACE_CONTACT_LENS_BUNDLE_BENELUX_CART_LINE_ITEM);
    const handleOnCompleted = React__default.useCallback((replacedLineItem, data, onSuccess, onValidationError, onError)=>{
        const result = data.replaceContactLensBundleBeneluxCartLineItem;
        if (!result.success) {
            const validationErrors = getValidationErrorCodes(result.validationErrors);
            onValidationError && onValidationError(validationErrors);
            return;
        }
        if (!result.cart) {
            if (onError) {
                onError(errorsMapping.CART_IS_EMPTY);
            }
            return;
        }
        const lineItemsInCart = result.cart.lineItems;
        const addedLineItems = lineItemsInCart.filter(({ sku  })=>sku === replacedLineItem.newSku
        );
        const addedLineItemsMappedToViewModel = lineItemDataMapper(addedLineItems, settings, intl);
        const dataLayerEvents = mapLineItemToDataLayerEvent(DataLayer.DataLayerTypes.EventType.add, addedLineItemsMappedToViewModel, result.cart.id);
        dataLayerEvents && DataLayer.pushEcommerceEvent(dataLayerEvents);
        dispatchMiniCartEvent('updated', addedLineItemsMappedToViewModel);
        const SdProductObjects = addedLineItemsMappedToViewModel.map(createProductObject);
        CartV2Actions.addToCartSdAction(SdProductObjects);
        onSuccess && onSuccess(addedLineItemsMappedToViewModel);
    }, [
        intl,
        settings
    ]);
    const replaceLineItem = React__default.useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(lineItemDrafts, onSuccess, onValidationError, onError) {
            if (!cart.cartId) {
                if (onError) {
                    onError(errorsMapping.MISSING_CART_ID);
                }
                return;
            }
            try {
                const { data , errors  } = yield replaceCartLineItem({
                    variables: {
                        cartInput: {
                            cartId: cart.cartId,
                            lineItemToReplace: lineItemDrafts
                        }
                    }
                });
                if (errors) {
                    if (onError) {
                        onError(errorsMapping.SOMETHING_WENT_WRONG);
                    }
                }
                if (data) {
                    handleOnCompleted(lineItemDrafts, data, onSuccess, onValidationError, onError);
                }
            } catch (error) {
                if (onError) {
                    onError(errorsMapping.SOMETHING_WENT_WRONG);
                }
            }
        });
        return function(lineItemDrafts, onSuccess, onValidationError, onError) {
            return _ref.apply(this, arguments);
        };
    }(), [
        cart.cartId,
        handleOnCompleted,
        replaceCartLineItem
    ]);
    const { called , loading , error , data: data1  } = replaceCartLineItemMutation;
    var _cart;
    const newCart = (_cart = data1 === null || data1 === void 0 ? void 0 : data1.replaceContactLensBundleBeneluxCartLineItem.cart) !== null && _cart !== void 0 ? _cart : null;
    return [
        replaceLineItem,
        {
            called,
            loading,
            error,
            cart: newCart
        }
    ];
};

export { useReplaceLineItemContactLensesBundleBenelux };
//# sourceMappingURL=use-replace-line-item-contact-lenses-bundle-benelux.js.map
